import { useMemo } from "react";
import { useLocation } from "wouter";
import { Tag, ArrowRight } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { CaptureButton } from "@/components/PageHeader";
import { useItemsStore } from "@/lib/stores/itemsStore";

export default function Etiquetas() {
  const items = useItemsStore((s) => s.items);
  const [, navigate] = useLocation();

  const tags = useMemo(() => {
    const counts = {};
    for (const item of items) for (const t of item.tags) counts[t] = (counts[t] || 0) + 1;
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [items]);

  const max = tags[0]?.count || 1;

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="tags-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Etiquetas</h1>
            <p className="mt-1.5 text-muted-foreground text-[15px]">
              Las propone el agente al enriquecer y puedes editarlas en cada contenido.
            </p>
          </div>
          <CaptureButton />
        </div>

        {tags.length === 0 ? (
          <div className="rounded-lg border bg-card">
            <EmptyState icon={Tag} title="Todavía no hay etiquetas." hint="Comparte contenido y el agente las propondrá." />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {tags.map((t) => (
              <button
                key={t.name}
                type="button"
                onClick={() => navigate("/biblioteca")}
                data-testid={`tag-card-${t.name}`}
                className="rounded-lg border bg-card p-4 text-left hover:border-teal-300/60 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-accent grid place-items-center shrink-0">
                    <Tag className="h-4 w-4 text-primary" strokeWidth={1.75} />
                  </span>
                  <span className="font-mono text-sm font-medium flex-1 truncate">#{t.name}</span>
                  <span className="text-sm text-muted-foreground tabular-nums">{t.count}</span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-primary/70" style={{ width: `${(t.count / max) * 100}%` }} />
                </div>
                <span className="mt-2.5 flex items-center gap-1 text-[13px] text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Ver contenidos <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
